import React, { useState } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAnalytics } from '../../services/useAnalytics'
import TrendCombo from './TrendCombo'
import TopSitesBar from './TopSitesBar'
import MixRatios from './MixRatios'
import PerformanceGauge from './PerformanceGauge'

import Sidebar from '../Sidebar'
import UserDropdown from '../UserDropdown'
import AppLauncher from '../AppLauncher'
import { useDarkMode } from '../../context/DarkModeContext'
import { useSidebar } from '../../context/SidebarContext'

dayjs.extend(relativeTime)

export default function AnalyticsDashboard() {
	const { isDarkMode } = useDarkMode()
	const { sidebarWidth } = useSidebar()

	const presetOptions = [
		{ label: 'Last 24 hours', value: '24h' },
		{ label: 'Last 7 days', value: '7d' },
	]

	const [preset, setPreset] = useState('24h')
	const { data, isLoading, error } = useAnalytics(preset)

	return (
		<div style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}>
			<Sidebar />

			<div
				style={{
					paddingLeft: `${sidebarWidth}px`,
					transition: 'padding-left 300ms ease-in-out',
				}}
			>
			{/* Top nav */}
			<div
				className="top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 px-4 sm:px-6 lg:px-8"
				style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}
			>
				<div className="flex items-center ml-auto gap-x-3">
					<AppLauncher />
					<UserDropdown />
				</div>
			</div>

			{/* Content */}
			<main className={`overflow-hidden rounded-tl-lg ${isDarkMode ? 'bg-dark-color' : 'bg-white'}`}>
				<div className={`${isDarkMode ? 'bg-dark-color' : 'bg-white'}`}>
					{isLoading ? (
						<div className="flex flex-col justify-center h-screen">
							<h1
								className={`text-center ${
									isDarkMode ? 'text-white' : 'text-black'
								} text-4xl mb-96`}
							>
								Loading...
							</h1>
						</div>
					) : error ? (
						<p className="p-6 text-red-500">Error loading analytics</p>
					) : (
						<div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-7xl mx-auto space-y-3">
							{/* date picker and timestamp on same line */}
							<div className="flex items-center gap-4">
								<select
									value={preset}
									onChange={e => setPreset(e.target.value)}
									className={`text-sm border border-gray-600 rounded px-3 py-1 pr-8 min-w-[120px] ${
										isDarkMode
											? 'bg-dark-color text-white'
											: 'bg-white text-black'
									}`}
								>
									{presetOptions.map(o => (
										<option
											key={o.value}
											value={o.value}
											className={
												isDarkMode
													? 'bg-dark-color text-white'
													: 'bg-white text-black'
											}
										>
											{o.label}
										</option>
									))}
								</select>
							</div>

							<TrendCombo daily={data.trend.daily} totals={data.trend.totals} />

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<PerformanceGauge
									pct={data.carrierPerformance}
									isDarkMode={isDarkMode}
								/>
								<MixRatios
									stopMix={data.stopMix}
									teamMix={data.teamMix}
									distanceMix={data.distanceMix}
									isDarkMode={isDarkMode}
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<TopSitesBar
									data={{
										facility: data.topOrigins.facility,
										city: data.topOrigins.city,
										state: data.topOrigins.state,
									}}
									title="Top Origins"
								/>
								<TopSitesBar
									data={{
										facility: data.topDestinations.facility,
										city: data.topDestinations.city,
										state: data.topDestinations.state,
									}}
									title="Top Destinations"
								/>
							</div>
						</div>
					)}
				</div>
			</main>
			</div>
		</div>
	)
}
